import { useState, type ReactNode } from "react";
import { Gift } from "lucide-react";

import { Button } from "components/common/buttons/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/TooltipV3";
import { ReferralInviteDialog } from "features/referral/components/ReferralInviteDialog";
import { useSessionUser } from "hooks/useSession";

export const REFERRAL_LABEL = "Refer a friend";

export interface HeaderAction {
  /** The action applies to this account and should be offered. */
  available: boolean;
  open: () => void;
  /** Mount once; the icon button and the More menu share it. */
  dialog: ReactNode;
}

/**
 * The referral invite, for accounts that buy project licenses one at a
 * time (the ones referral bonuses pay out to).
 */
export function useReferralAction(): HeaderAction {
  const { isOneTimePurchase } = useSessionUser();
  const [isOpen, setIsOpen] = useState(false);
  const available = Boolean(isOneTimePurchase);

  return {
    available,
    open: () => setIsOpen(true),
    dialog: available ? (
      <ReferralInviteDialog isOpen={isOpen} onClose={() => setIsOpen(false)} />
    ) : null,
  };
}

/** The gift icon button in the header. */
export const ReferralIconButton = ({ onOpen }: { onOpen: () => void }) => (
  <Tooltip delayDuration={400}>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={REFERRAL_LABEL}
        onClick={onOpen}
        className="inline-flex h-8 w-8 min-w-0 shrink-0 items-center justify-center p-0 rounded-md text-foreground hover:bg-muted"
      >
        <Gift className="h-4 w-4" aria-hidden />
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom" align="end" sideOffset={6} className="max-w-xs">
      Free licenses when you refer a friend
    </TooltipContent>
  </Tooltip>
);

/** Header shortcut to the referral invite dialog: the icon button and its dialog. */
export const ReferralButton = () => {
  const referral = useReferralAction();
  if (!referral.available) return null;

  return (
    <>
      <ReferralIconButton onOpen={referral.open} />
      {referral.dialog}
    </>
  );
};
